import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import KeyValuePairs from '@amzn/awsui-components-console/key-value-pairs';
import Header from '@amzn/awsui-components-console/header';
import Spinner from '@amzn/awsui-components-console/spinner';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Box from '@amzn/awsui-components-console/box';
import Container from '@amzn/awsui-components-console/container';
import Button from '@amzn/awsui-components-console/button';
import ProgressBar from '@amzn/awsui-components-console/progress-bar';
import ShowSnowStatus from './ShowSNOWStatus';
import CopyableText from './CopyableText';
import { getEntityProperties } from 'src/utils/AttributeUtils';
import { getFullDateFromEpoch } from 'src/utils/CommonUtils';
import { UserIcon } from '../commonComponents/UserIcon';
import EntityAttributesTable from './EntityAttributesTable';
import { EntityAttributes } from 'src/@types/snowDataServiceTypes/createEntity.interface';
import './entityCard.scss';

const EntityCard = (props) => {
	const {
		entityData,
		status,
		lastStatusUpdateTime,
		workflowType,
		taskExecutionData,
		finishedTasks = 0,
		totalTasks = 0,
	} = props;
	const entityAttributes = entityData.EntityAttributes;
	const entityType = entityData.EntityType;
	const entityName = entityData.EntityName;

	const taskIdToName = useMemo(() => (taskExecutionData || []).reduce((map, task) => {
		if (task.taskId && task.taskName) {
			map[task.taskId] = task.taskName;
		}

		if (task.childWorkflowId && task.taskName) {
			map[task.childWorkflowId] = task.taskName;
		}
		return map;
	}, {} as Record<string, string>), [taskExecutionData]);

	const taskAttributeSections = useMemo(() => {
		const isAttributeGroup = (value: EntityAttributes[string]): value is EntityAttributes =>
			typeof value === 'object' &&
			value !== null &&
			!Array.isArray(value);

		const toSections = (
			attributes: EntityAttributes,
			parentNames: string[] = []
		): [string, EntityAttributes][] =>
			Object.entries(attributes).flatMap(([id, value]) => {
				if (!isAttributeGroup(value) || !Object.keys(value).length) return [];
				const names = [...parentNames, taskIdToName[id] || id];

				return Object.values(value).every(isAttributeGroup)
					? toSections(value, names)
					: [[names.join(' -> '), value]];
			});

		return isAttributeGroup(entityData.EntityData)
			? toSections(entityData.EntityData)
			: [];
	}, [entityData.EntityData, taskIdToName]);

	const isInProgress = status === 'IN PROGRESS';
	const progress = totalTasks > 0 ? Math.round((finishedTasks / totalTasks) * 100) : 0;

	const renderStatus = () => {
		if (status == null) {
			return <Spinner size="normal" />;
		}
		
		if (isInProgress && totalTasks > 0) {
			return (
				<ProgressBar
					value={progress}
					description={<ShowSnowStatus status={status} />}
					variant="key-value"
					status="in-progress"
					additionalInfo={`${finishedTasks} of ${totalTasks} tasks done`}
				/>
			);
		}
		
		return <ShowSnowStatus status={status} />;
	};

	return !entityAttributes ? (
		<div>Was not able to fetch attributes for {entityName} entity</div>
	) : (
		<Box>
			<SpaceBetween size="l">
				<Container
					header={
						<Header variant="h2">Entity Details for {entityName}</Header>
					}
				>
					<KeyValuePairs
						columns={3}
						items={Object.entries({
							...{
								[entityAttributes.submitted_by
									? 'Submitted By'
									: 'Created By']: (
									<UserIcon
										user={
											entityAttributes.submitted_by || entityData.CreatedBy
										}
										size='medium'
									/>
								),
							},
							Status:
									status == null ? (
										<Spinner size="normal" />
									) : (
										renderStatus()
									),
							'Workflow Definition Id': (
								<CopyableText textToCopy={workflowType} label="Workflow Definition Id">
									{workflowType}
								</CopyableText>
							),
							Region: entityData.Region,
							...(entityAttributes.time_zone && { Timezone: entityAttributes.time_zone }),
							...(entityAttributes.physical_country_full_name && { 'Physical Country': entityAttributes.physical_country_full_name }),
							...(entityAttributes.org_unit && { 'Org Unit': entityAttributes.org_unit }),
							...((entityAttributes.inbound_launch_date || entityAttributes.launch_date || entityAttributes.service_area_launch_date) && {
								'Inbound Launch Date': getFullDateFromEpoch(
									entityAttributes.inbound_launch_date ||
											entityAttributes.launch_date ||
											entityAttributes.service_area_launch_date
								),
							}),
							...(entityAttributes.outbound_launch_date && {
								'Outbound Launch Date': getFullDateFromEpoch(
									entityAttributes.outbound_launch_date
								),
							}),
							'Last updated on':
									getFullDateFromEpoch(lastStatusUpdateTime) ||
									'Workflow not yet started',
								'Entity Type': entityType,
								'Entity Properties': getEntityProperties(
									entityAttributes.node_properties
								),
								'Cycle Id': entityData.CycleId ? (
									<CopyableText textToCopy={String(entityData.CycleId)} label="Cycle Id">
										<Button
											variant="inline-link"
											href={`#/findCycle?cycleId=${entityData.CycleId}&workflowType=${workflowType}`}
										>
											{entityData.CycleId}
										</Button>
									</CopyableText>
								) : 'N/A',
							}).filter(([, value]) => value !== undefined && value !== null && value !== '').map(([label, value]) => ({
								label,
								value: (value || 'N/A') as React.ReactNode,
							}))}
						/>
					</Container>

				<EntityAttributesTable
					attributes={entityAttributes}
					title="Additional Entity Attributes"
				/>

				{taskAttributeSections.map(([name, attributes]) => (
					<EntityAttributesTable
						key={name}
						attributes={attributes}
						title={`Task Attributes: ${name}`}
					/>
				))}

			</SpaceBetween>
		</Box>
	);
};

EntityCard.propTypes = {
	entityData: PropTypes.object.isRequired,
	status: PropTypes.string.isRequired,
	lastStatusUpdateTime: PropTypes.string.isRequired,
	workflowType: PropTypes.string.isRequired,
	workflowDefinitionName: PropTypes.string.isRequired,
	taskExecutionData: PropTypes.array,
	finishedTasks: PropTypes.number,
	totalTasks: PropTypes.number,
};

export default EntityCard;
